import { Copy, TriangleAlert } from 'lucide-react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { toast } from '@/hooks/use-toast';
import { gql } from '@/gql/gql';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Logo } from '@/components/logos/logo';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CurrentMerchantForConsentQuery, SetConsentMutation } from '@/gql/graphql';

const CURRENT_MERCHANT_QUERY = gql(`
  query CurrentMerchantForConsent {
    currentMerchant {
      id
      hasConsentEnabled
    }
  }
`);

const GET_MERCHANT_ID = gql(/* GraphQL */ `
  query GetMerchantId {
    currentMerchant {
      id
      domain
    }
  }
`);

const STORE_DETAILS_QUERY = gql(`
  query StoreDetails {
    currentMerchant {
      id
    }
  }
`);
const SET_CONSENT_MUTATION = gql(`
  mutation SetConsent($consent: Boolean!) {
    setConsent(consent: $consent) {
      id
      hasConsentEnabled
    }
  }
`);
interface StoreDetailsQuery {
  currentMerchant: {
    id: string;
  };
}

const formSchema = z.object({
  hasConsentBanner: z.enum(['yes', 'no'])
});

export function ShopifyConsent() {
  const { data } = useQuery(GET_MERCHANT_ID);
  const { data: storeDetails } = useQuery<StoreDetailsQuery>(STORE_DETAILS_QUERY);
  const { data: currentMerchantData } =
    useQuery<CurrentMerchantForConsentQuery>(CURRENT_MERCHANT_QUERY);
  const [setConsent] = useMutation<SetConsentMutation>(SET_CONSENT_MUTATION);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      hasConsentBanner: currentMerchantData?.currentMerchant.hasConsentEnabled ? 'yes' : 'no'
    }
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    void setConsent({
      variables: {
        consent: values.hasConsentBanner === 'yes'
      }
    });

    toast({
      title: 'Consent updated',
      description: 'Your consent settings have been updated.',
      variant: 'default'
    });
  };

  const getCustomPixelScript = () => {
    const domain = data?.currentMerchant.domain ?? 'your-shop-domain';

    return `
const GTM_CONTAINER_ID = 'GTM-XXXXXXXX';
const load = (host) =>
  import(host + "/worker-sdk/web-pixel").then((pixel) => ({ pixel, host }));

const execute = async (api, gtmContainerId) => {
  // Falls back to Contextual's shared endpoint if ctx.${domain} is not set up.
  const { pixel, host } = await load("https://ctx.${domain}").catch(() =>
    load("https://merchants.getcontextual.io")
  );
  await pixel.contextualWebAppPixel(api, gtmContainerId, { host });
};

execute({ analytics, browser, init }, GTM_CONTAINER_ID);
    `.trim();
  };

  const copyToClipboard = async (text: string) => {
    if (data?.currentMerchant.domain) {
      await navigator.clipboard.writeText(text);
      toast({
        title: 'Copied to clipboard!',
        variant: 'default'
      });
    } else {
      toast({
        title: 'Unable to copy script',
        description: 'Shop domain is not available. Please try again later.',
        variant: 'destructive'
      });
    }
  };

  return (
    <Form {...form}>
      <form
        id="consent-form"
        onSubmit={(e) => {
          void form.handleSubmit(onSubmit)(e);
        }}
        className="space-y-8 px-4 sm:px-6 lg:px-8"
      >
        <div className="mx-auto py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Logo icon="shopify" height={30} width={30} />
              <h1 className="text-2xl sm:text-3xl font-medium text-foreground">Shopify</h1>
            </div>
            <Button
              type="submit"
              form="consent-form"
              disabled={!form.formState.isValid || !form.formState.isDirty}
            >
              Save Changes
            </Button>
          </div>
          <div className="flex flex-wrap gap-6 lg:gap-8 mt-8">
            <div className="w-full lg:w-[325px]">
              <div className="font-semibold text-lg sm:text-xl text-foreground">
                Store Connection
              </div>
              <div className="font-normal text-sm sm:text-base text-muted-foreground mt-2">
                This is your Shopify store&apos;s myShopify URL. This is what uniquely identifies
                your store to Contextual and cannot be changed.
              </div>
            </div>
            <div className="w-full lg:w-[450px]">
              <div className="text-sm font-medium text-foreground">Store URL</div>
              <div className="flex items-center gap-2 mt-2">
                <div className="flex rounded-lg overflow-hidden border border-gray-200 bg-gray-50 ">
                  <span className="px-3 py-2 text-sm text-foreground border-r border-gray-200">
                    http://
                  </span>
                  <span className="px-3 py-2 text-sm font-normal text-muted-foreground">
                    {storeDetails?.currentMerchant.id}
                  </span>
                </div>
                <Button
                  variant="secondary"
                  className="border mt-2 lg:mt-0"
                  onClick={() => {
                    void copyToClipboard(storeDetails?.currentMerchant.id ?? '');
                  }}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy
                </Button>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-6 lg:gap-8 mt-8">
            <div className="w-full lg:w-[325px]">
              <div className="font-semibold text-lg sm:text-xl text-foreground">
                Consent Settings
              </div>
              <div className="font-normal text-sm sm:text-base text-muted-foreground mt-2">
                If you are using a consent banner, Contextual can integrate with it to control
                events guarded by user consent.
              </div>
            </div>
            <div className="w-full lg:w-[675px]">
              <div className="text-base text-foreground font-semibold">
                Are you using a consent banner?
              </div>
              <FormField
                control={form.control}
                name="hasConsentBanner"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className="flex flex-col space-y-2 mt-6"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="no" id="no" />
                          <Label htmlFor="no">No</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="yes" id="yes" />
                          <Label htmlFor="yes">Yes</Label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-6 lg:gap-8 mt-8">
            <div className="w-full lg:w-[325px]">
              <div className="font-semibold text-lg sm:text-xl text-foreground">Web Pixel</div>
              <div className="font-normal text-sm sm:text-base text-muted-foreground mt-2">
                Paste Contextual’s Custom Web Pixel snippet into your Shopify Settings.
              </div>
            </div>
            <div className="w-full lg:w-[675px]">
              <div className="font-normal text-sm sm:text-base text-muted-foreground">
                In your Shopify Settings, this script needs to be pasted into the custom pixel code
                editor and named “Contextual”. The Contextual app should also be installed, and
                enabled in the Shopify settings.
              </div>
              <div className="flex flex-wrap space-x-3 mt-4">
                <Input
                  placeholder={getCustomPixelScript()}
                  className="pr-20 rounded-md border border-gray-300 w-full lg:w-[373px]"
                  disabled={true}
                />
                <Button
                  variant="secondary"
                  className="border mt-2 lg:mt-0"
                  onClick={() => {
                    void copyToClipboard(getCustomPixelScript());
                  }}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copy
                </Button>
              </div>
              <Alert className="mt-4 text-warn-foreground bg-warn" variant="warning">
                <TriangleAlert size={16} />
                <div className="text-sm text-foreground font-normal"></div>
                <AlertTitle>Important!</AlertTitle>
                <AlertDescription>{` Please also replace the pasted placeholder GTM container ID (e.g. GTM-XXXXXXX) with your own container
                  ID.`}</AlertDescription>
              </Alert>
            </div>
          </div>
        </div>
      </form>
    </Form>
  );
}
